import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type SearchHit } from '../lib/api.js';
import { useGm } from '../hooks/useGm.js';
import { DvIcon } from '../components/DvIcon.js';
import { LinkTextarea, TagInput } from '../components/wiki/LinkField.js';
import { BlockPreview } from '../components/wiki/BlockPreview.js';
import { SCHEMAS, CREATE_ORDER, getSchema } from '../lib/entitySchemas.js';
import {
  PROPS, GROUP_ORDER, propsByGroup, TEMPLATES, newBlock, templateBlocks, slugify, compile,
  ZONE_COLOR, ZONE_LABEL, type Block, type PField, type Core,
} from '../lib/entityBlocks.js';
import '../styles/wiki.css';

export function EntityCreate(): JSX.Element {
  const [params] = useSearchParams();
  const { slug: editSlug } = useParams<{ slug: string }>();
  const editing = !!editSlug;
  const { isGm } = useGm();
  const navigate = useNavigate();
  const [editType, setEditType] = useState('');
  const [loaded, setLoaded] = useState(!editing);
  const type = editing ? editType : (params.get('type') ?? '');
  const schema = getSchema(type);

  const [name, setName] = useState('');
  const [nameOrig, setNameOrig] = useState('');
  const [epithet, setEpithet] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [previewGm, setPreviewGm] = useState(true);
  const [search, setSearch] = useState('');
  const [tplOpen, setTplOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; pos: 'before' | 'after' } | null>(null);

  useEffect(() => {
    if (editing) return; // create-mode: скидаємо форму під обраний тип
    setName(params.get('title_uk') ?? '');
    setNameOrig(params.get('title_en') ?? '');
    setEpithet(''); setSlug(params.get('slug') ?? ''); setSlugTouched(false);
    setBlocks(type ? templateBlocks(type) : []); setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!editSlug) return;
    api.wikiPage(editSlug).then((p) => {
      const f = p.frontmatter as Record<string, unknown>;
      setEditType(p.type); setName(p.titleUk);
      setNameOrig(typeof f.native === 'string' ? f.native : (p.titleEn || ''));
      setEpithet(typeof f.epithet === 'string' ? f.epithet : '');
      setSlug(p.slug); setSlugTouched(true);
      const raw = Array.isArray(f.blocks) ? (f.blocks as Array<{ type: string; gm?: boolean; data?: Record<string, unknown> }>) : [];
      setBlocks(raw.map((b, i) => ({ id: `e${i}-${b.type}`, type: b.type, gm: !!b.gm, collapsed: false, data: b.data ?? {} })));
      setLoaded(true);
    }).catch((e) => { setErr(String(e)); setLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSlug]);

  const effSlug = (slugTouched && slug) ? slug : (slug || slugify(nameOrig || name));
  const setType = (t: string): void => navigate(t ? `/wiki/new?type=${t}` : '/wiki/new', { replace: true });
  const core: Core = { type, name, nameOriginal: nameOrig, epithet, slug: effSlug };

  function addBlock(pid: string): void { setBlocks((b) => [...b, newBlock(pid)]); }
  function patchBlock(id: string, patch: Partial<Block>): void { setBlocks((b) => b.map((x) => (x.id === id ? { ...x, ...patch } : x))); }
  function patchData(id: string, key: string, val: unknown): void { setBlocks((b) => b.map((x) => (x.id === id ? { ...x, data: { ...x.data, [key]: val } } : x))); }
  function removeBlock(id: string): void { setBlocks((b) => b.filter((x) => x.id !== id)); }
  function moveBlock(id: string, dir: -1 | 1): void {
    setBlocks((b) => { const i = b.findIndex((x) => x.id === id); const j = i + dir; if (i < 0 || j < 0 || j >= b.length) return b; const c = [...b]; [c[i], c[j]] = [c[j]!, c[i]!]; return c; });
  }
  function dropReorder(targetId: string, pos: 'before' | 'after'): void {
    setBlocks((b) => {
      if (!dragId || dragId === targetId) return b;
      const moved = b.find((x) => x.id === dragId); if (!moved) return b;
      const without = b.filter((x) => x.id !== dragId);
      let ti = without.findIndex((x) => x.id === targetId); if (ti < 0) return b;
      if (pos === 'after') ti += 1;
      without.splice(ti, 0, moved); return without;
    });
    setDragId(null); setOver(null);
  }

  async function save(): Promise<void> {
    setErr(null);
    if (!name.trim()) { setErr('Вкажіть назву в ядрі.'); return; }
    const finalSlug = editing ? (editSlug ?? '') : effSlug;
    if (!finalSlug) { setErr('Вкажіть slug.'); return; }
    setBusy(true);
    try {
      const out = compile({ ...core, name: name.trim(), slug: finalSlug }, blocks);
      if (editing) await api.wikiUpdate(finalSlug, out); else await api.wikiCreate(out);
      navigate(`/wiki/${encodeURIComponent(finalSlug)}`);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  }

  if (!isGm) return <div className="dv-fullpage"><Link to="/wiki" className="dv-link-muted"><DvIcon name="back" size={14} /> До світу</Link><div className="dv-empty">Редагування доступне лише в режимі Майстра. Натисни <b>МАЙСТЕР</b>.</div></div>;
  if (editing && !loaded) return <div className="dv-fullpage"><div className="dv-empty">Завантаження…</div></div>;

  // ── Вибір типу ──
  if (!schema) {
    return (
      <div className="dv-fullpage">
        <Link to="/wiki" className="dv-link-muted"><DvIcon name="back" size={14} /> До світу</Link>
        <p className="dv-create-eyebrow" style={{ marginTop: 18 }}>Нова сутність</p>
        <h1 className="dv-create-h1">Що створюємо?</h1>
        <p className="dv-create-sub">Оберіть тип — ядро отримає базові блоки, які можна доточувати.</p>
        <div className="dv-typegrid">{CREATE_ORDER.map((t) => { const sc = SCHEMAS[t]; if (!sc) return null; const ac = `var(--${sc.cat})`; return (
          <button key={t} type="button" className="dv-typecard" onClick={() => setType(t)}>
            <span className="dv-typecard__ico" style={{ background: `color-mix(in srgb, ${ac} 14%, transparent)`, color: ac }}><DvIcon name={sc.icon} size={24} /></span>
            <span className="dv-typecard__label">{sc.label}</span>
          </button>); })}</div>
      </div>
    );
  }

  const accent = `var(--${schema.cat})`;
  const occupied = new Set(blocks.map((b) => b.type));

  return (
    <div className="dv-builder">
      {/* ── ЯДРО ── */}
      <div className="dv-core">
        <button type="button" className="dv-core__back" onClick={() => navigate('/wiki')} title="До світу"><DvIcon name="back" size={18} /></button>
        <span className="dv-core__ico" style={{ background: `color-mix(in srgb, ${accent} 16%, transparent)`, color: accent }}><DvIcon name={schema.icon} size={26} /></span>
        <div className="dv-core__id">
          <div className="dv-core__meta">{editing ? 'Редагування' : 'Нова сутність'} · {schema.label}</div>
          <div className="dv-core__namerow">
            <input className="dv-core__name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Назва" autoFocus />
            <input className="dv-core__orig" value={nameOrig} onChange={(e) => setNameOrig(e.target.value)} placeholder="оригінальна назва" />
          </div>
          <input className="dv-core__epithet" value={epithet} onChange={(e) => setEpithet(e.target.value)} placeholder="епітет / підзаголовок" />
        </div>
        <div className="dv-core__right">
          <span className="dv-chip dv-chip--solid" style={{ background: accent, color: '#fff', border: 'none' }}>{schema.category}</span>
          <span className="dv-core__slug"><DvIcon name="link" size={12} /><input value={effSlug} readOnly={editing} onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }} /></span>
          <button type="button" className="dv-btn dv-btn--primary" disabled={busy} onClick={save}><DvIcon name="check" size={16} /> {busy ? 'Зберігаю…' : editing ? 'Оновити' : 'Зберегти'}</button>
        </div>
      </div>

      {err && <div className="wiki-error" style={{ margin: '8px 16px 0' }}>⚠ {err}</div>}

      <div className="dv-builder__body">
        {/* ── ПАЛІТРА ── */}
        <aside className="dv-palette">
          <div className="dv-palette__search"><DvIcon name="search" size={15} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Знайти властивість…" /></div>
          <div className="dv-tpl">
            <button type="button" className="dv-tpl__btn" onClick={() => setTplOpen((v) => !v)}>
              <span><span className="dv-tpl__meta">Базовий шаблон</span><span className="dv-tpl__name">{schema.label}</span></span><DvIcon name="chevron" size={14} />
            </button>
            {tplOpen && (
              <div className="dv-tpl__menu">
                {Object.keys(TEMPLATES).filter((k) => k !== 'blank').map((k) => { const sc = SCHEMAS[k]; if (!sc) return null; return (
                  <button key={k} type="button" className="dv-tpl__item" onClick={() => {
                    setTplOpen(false);
                    if (k !== type) navigate(`/wiki/new?type=${k}${name.trim() ? `&title_uk=${encodeURIComponent(name.trim())}` : ''}`, { replace: true });
                    else setBlocks(templateBlocks(k));
                  }}><DvIcon name={sc.icon} size={15} color={`var(--${sc.cat})`} /> {sc.label}{k === type ? ' ✓' : ''}</button>); })}
                <button type="button" className="dv-tpl__item" onClick={() => { setBlocks([]); setTplOpen(false); }}><DvIcon name="document" size={15} /> Чистий аркуш</button>
                <div className="dv-tpl__hint">Застосування досипає дефолтні блоки.</div>
              </div>
            )}
          </div>
          {GROUP_ORDER.map((g) => {
            const items = propsByGroup(g).filter((p) => !search.trim() || p.label.toLowerCase().includes(search.trim().toLowerCase()));
            if (!items.length) return null;
            return (
              <div key={g} className="dv-palette__group">
                <div className="dv-palette__gtitle"><span className="dv-palette__gdot" style={{ background: ZONE_COLOR[items[0]!.zone] }} />{g}</div>
                {items.map((p) => { const taken = !p.repeatable && occupied.has(p.id); return (
                  <button key={p.id} type="button" className={`dv-addchip ${taken ? 'taken' : ''}`} disabled={taken} onClick={() => addBlock(p.id)}>
                    <DvIcon name={p.icon} size={16} color={ZONE_COLOR[p.zone]} /><span>{p.label}</span>
                    {taken ? <DvIcon name="check" size={13} color="var(--ok)" /> : <DvIcon name="plus" size={13} color="var(--dv-ink-faint)" />}
                  </button>); })}
              </div>
            );
          })}
        </aside>

        {/* ── ПОЛОТНО ── */}
        <main className="dv-canvas">
          {blocks.length === 0 && <div className="dv-canvas__empty"><DvIcon name="plus" size={26} color="var(--dv-ink-faint)" /><div className="dv-canvas__emptyh">Картка майже порожня</div><div className="dv-canvas__emptyt">Додай першу властивість зліва або обери базовий шаблон.</div></div>}
          {blocks.map((b, i) => (
            <BlockEditor key={b.id} b={b} first={i === 0} last={i === blocks.length - 1}
              dragging={dragId === b.id} over={over?.id === b.id ? over.pos : null}
              onDragStart={() => setDragId(b.id)} onDragEnd={() => { setDragId(null); setOver(null); }}
              onDragOver={(pos) => { if (dragId && dragId !== b.id) setOver({ id: b.id, pos }); }}
              onDrop={() => dropReorder(b.id, over?.pos ?? 'before')}
              onData={(k, v) => patchData(b.id, k, v)} onPatch={(p) => patchBlock(b.id, p)} onRemove={() => removeBlock(b.id)} onMove={(d) => moveBlock(b.id, d)} />
          ))}
          {blocks.length > 0 && <div className="dv-canvas__addhint">Додай ще властивість зліва ↤</div>}
        </main>

        {/* ── ПРЕВʼЮ ── */}
        <aside className="dv-previewpane">
          <div className="dv-previewpane__bar">
            <span className="dv-formsec__t" style={{ margin: 0, border: 0, padding: 0 }}>Превʼю</span>
            <div className="dv-seg dv-seg--mp">
              <button type="button" className={previewGm ? 'on' : ''} onClick={() => setPreviewGm(true)}>Майстер</button>
              <button type="button" className={!previewGm ? 'on' : ''} onClick={() => setPreviewGm(false)}>Гравець</button>
            </div>
          </div>
          <div className="dv-previewpane__body"><BlockPreview core={core} blocks={blocks} gm={previewGm} /></div>
        </aside>
      </div>
    </div>
  );
}

/* ── Редактор одного блоку ── */
function BlockEditor({ b, first, last, dragging, over, onDragStart, onDragEnd, onDragOver, onDrop, onData, onPatch, onRemove, onMove }: {
  b: Block; first: boolean; last: boolean; dragging: boolean; over: 'before' | 'after' | null;
  onDragStart: () => void; onDragEnd: () => void; onDragOver: (pos: 'before' | 'after') => void; onDrop: () => void;
  onData: (k: string, v: unknown) => void; onPatch: (p: Partial<Block>) => void; onRemove: () => void; onMove: (d: -1 | 1) => void;
}): JSX.Element {
  const def = PROPS[b.type]!;
  const zoneCol = ZONE_COLOR[def.zone];
  const [menu, setMenu] = useState(false);
  return (
    <div
      className={`dv-block ${b.gm ? 'gm' : ''} ${dragging ? 'dragging' : ''} ${over ? 'over-' + over : ''}`}
      draggable
      onDragStart={(e) => { const t = e.target as HTMLElement; if (t.closest('input,textarea,select,button')) { e.preventDefault(); return; } e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', b.id); onDragStart(); }}
      onDragOver={(e) => { e.preventDefault(); const r = e.currentTarget.getBoundingClientRect(); onDragOver(e.clientY < r.top + r.height / 2 ? 'before' : 'after'); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      onDragEnd={onDragEnd}
    >
      <span className="dv-block__stripe" style={{ background: b.gm ? 'var(--fear)' : zoneCol }} />
      <div className="dv-block__body">
        <div className="dv-block__head">
          <span className="dv-block__grip" title="Перетягнути для зміни порядку" aria-hidden="true">
            <svg width="11" height="16" viewBox="0 0 11 16" fill="currentColor"><circle cx="3" cy="3" r="1.3"/><circle cx="8" cy="3" r="1.3"/><circle cx="3" cy="8" r="1.3"/><circle cx="8" cy="8" r="1.3"/><circle cx="3" cy="13" r="1.3"/><circle cx="8" cy="13" r="1.3"/></svg>
          </span>
          <DvIcon name={def.icon} size={16} color={b.gm ? 'var(--fear)' : zoneCol} />
          <span className="dv-block__name">{def.label}</span>
          <span className="dv-block__zone" style={{ color: b.gm ? 'var(--fear-deep)' : zoneCol }}>{b.gm ? 'GM' : ZONE_LABEL[def.zone]}</span>
          <button type="button" className="dv-block__ibtn" onClick={() => onPatch({ collapsed: !b.collapsed })} title="Згорнути"><DvIcon name="chevron" size={15} /></button>
          <div className="dv-block__menuwrap">
            <button type="button" className="dv-block__ibtn" onClick={() => setMenu((v) => !v)} title="Меню">⋯</button>
            {menu && (
              <div className="dv-block__menu" onMouseLeave={() => setMenu(false)}>
                <button type="button" disabled={first} onClick={() => { onMove(-1); setMenu(false); }}>↑ Вище</button>
                <button type="button" disabled={last} onClick={() => { onMove(1); setMenu(false); }}>↓ Нижче</button>
                {!def.gmAlways && <button type="button" onClick={() => { onPatch({ gm: !b.gm }); setMenu(false); }}>{b.gm ? 'Зробити публічним' : 'Зробити GM-only'}</button>}
                <button type="button" className="del" onClick={() => { onRemove(); setMenu(false); }}>Видалити</button>
              </div>
            )}
          </div>
        </div>
        {!b.collapsed && (
          <div className="dv-block__fields">
            {def.fields.map((f) => <FieldEditor key={f.key} f={f} value={b.data[f.key]} onChange={(v) => onData(f.key, v)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Редактор поля за kind ── */
function FieldEditor({ f, value, onChange }: { f: PField; value: unknown; onChange: (v: unknown) => void }): JSX.Element {
  const sval = typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
  const wide = ['richtext', 'textarea', 'segment', 'relation', 'rows', 'checkrows', 'tags', 'image', 'rellist'].includes(f.kind);
  const cls = `dv-field${wide ? ' dv-field--wide' : ''}`;
  if (f.kind === 'image') return <label className={cls}><span className="dv-field__lbl">{f.label}</span><input value={sval} onChange={(e) => onChange(e.target.value)} placeholder="https://…" />{sval && <img src={sval} alt="" className="dv-imgprev" />}</label>;
  if (f.kind === 'rellist') return <div className={cls}><span className="dv-field__lbl">{f.label}</span><RelListField value={(value as Array<{ slug: string; title: string }>) ?? []} onChange={onChange} /></div>;
  if (f.kind === 'richtext') return <label className={cls}><span className="dv-field__lbl">{f.label}</span><LinkTextarea value={sval} onChange={onChange} rows={2} placeholder={f.placeholder ?? '[[ — згадка сторінки…'} /></label>;
  if (f.kind === 'textarea') return <label className={cls}><span className="dv-field__lbl">{f.label}</span><textarea value={sval} onChange={(e) => onChange(e.target.value)} rows={2} placeholder={f.placeholder} /></label>;
  if (f.kind === 'tags') return <label className={cls}><span className="dv-field__lbl">{f.label}</span><TagInput value={sval} onChange={onChange} placeholder={f.placeholder} /></label>;
  if (f.kind === 'enum') return <label className={cls}><span className="dv-field__lbl">{f.label}</span><select value={sval} onChange={(e) => onChange(e.target.value)}><option value="">—</option>{f.options!.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>;
  if (f.kind === 'segment') return <div className={cls}><span className="dv-field__lbl">{f.label}</span><div className="dv-seg dv-seg--wide">{f.options!.map((o) => <button key={o} type="button" className={sval === o ? 'on' : ''} onClick={() => onChange(o)}>{o}</button>)}</div></div>;
  if (f.kind === 'relation') return <label className={cls}><span className="dv-field__lbl">{f.label}</span><RelField value={value as { slug: string; title: string } | undefined} onChange={onChange} /></label>;
  if (f.kind === 'rows' || f.kind === 'checkrows') return <div className={cls}><span className="dv-field__lbl">{f.label}</span><RowsEditor f={f} value={(value as Array<Record<string, unknown>>) ?? []} onChange={onChange} check={f.kind === 'checkrows'} /></div>;
  return <label className={cls}><span className="dv-field__lbl">{f.label}</span><input value={sval} onChange={(e) => onChange(e.target.value)} inputMode={f.kind === 'int' || f.kind === 'signed' || f.kind === 'pips' ? 'numeric' : undefined} placeholder={f.placeholder ?? (f.kind === 'pips' ? 'к-сть слотів' : '')} /></label>;
}

function RelField({ value, onChange }: { value?: { slug: string; title: string }; onChange: (v: unknown) => void }): JSX.Element {
  const [q, setQ] = useState(''); const [hits, setHits] = useState<SearchHit[]>([]);
  useEffect(() => { if (q.trim().length < 1) { setHits([]); return; } const t = setTimeout(() => api.wikiSearch(q, 6).then((r) => setHits(r.hits)).catch(() => setHits([])), 160); return () => clearTimeout(t); }, [q]);
  if (value?.slug) return <div className="dv-relchip" style={{ alignSelf: 'flex-start' }}><DvIcon name="link" size={12} /> {value.title}<button type="button" onClick={() => onChange(undefined)} aria-label="Прибрати"><DvIcon name="close" size={12} /></button></div>;
  return (
    <div className="dv-relpicker__search">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Шукати сторінку…" />
      {hits.length > 0 && <div className="dv-mention">{hits.map((h) => <button key={h.slug} type="button" className="dv-mention__item" onMouseDown={(e) => { e.preventDefault(); onChange({ slug: h.slug, title: h.title }); setQ(''); setHits([]); }}><span className="dv-mention__title">{h.title}</span>{h.category && <span className="dv-mention__cat">{h.category}</span>}</button>)}</div>}
    </div>
  );
}

function RelListField({ value, onChange }: { value: Array<{ slug: string; title: string }>; onChange: (v: unknown) => void }): JSX.Element {
  const [q, setQ] = useState(''); const [hits, setHits] = useState<SearchHit[]>([]);
  useEffect(() => { if (q.trim().length < 1) { setHits([]); return; } const t = setTimeout(() => api.wikiSearch(q, 6).then((r) => setHits(r.hits.filter((h) => !value.some((v) => v.slug === h.slug)))).catch(() => setHits([])), 160); return () => clearTimeout(t); }, [q, value]);
  return (
    <div>
      {value.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>{value.map((v) => <span key={v.slug} className="dv-relchip"><DvIcon name="link" size={12} /> {v.title}<button type="button" onClick={() => onChange(value.filter((x) => x.slug !== v.slug))} aria-label="Прибрати"><DvIcon name="close" size={12} /></button></span>)}</div>}
      <div className="dv-relpicker__search">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Додати сторінку…" />
        {hits.length > 0 && <div className="dv-mention">{hits.map((h) => <button key={h.slug} type="button" className="dv-mention__item" onMouseDown={(e) => { e.preventDefault(); onChange([...value, { slug: h.slug, title: h.title }]); setQ(''); setHits([]); }}><span className="dv-mention__title">{h.title}</span>{h.category && <span className="dv-mention__cat">{h.category}</span>}</button>)}</div>}
      </div>
    </div>
  );
}

function RowsEditor({ f, value, onChange, check }: { f: PField; value: Array<Record<string, unknown>>; onChange: (v: unknown) => void; check: boolean }): JSX.Element {
  const rows = value;
  const upd = (i: number, patch: Record<string, unknown>): void => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const rf = f.rowFields ?? [];
  return (
    <div className="dv-rows">
      {rows.map((r, i) => (
        <div key={i} className="dv-row">
          {check && <button type="button" className="dv-row__check" aria-pressed={!!r.done} onClick={() => upd(i, { done: !r.done })} style={{ background: r.done ? 'var(--ok)' : 'transparent' }}>{r.done ? <DvIcon name="check" size={12} color="#fff" /> : null}</button>}
          {rf.map((sf) => <input key={sf.key} value={typeof r[sf.key] === 'string' ? (r[sf.key] as string) : ''} onChange={(e) => upd(i, { [sf.key]: e.target.value })} placeholder={sf.label} style={{ flex: sf.key === 'desc' || sf.key === 'event' || sf.key === 'text' ? 2 : 1 }} />)}
          <button type="button" className="dv-row__del" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Прибрати"><DvIcon name="close" size={13} /></button>
        </div>
      ))}
      <button type="button" className="dv-feat-add" onClick={() => onChange([...rows, {}])}><DvIcon name="plus" size={13} stroke={2.2} /> Додати пункт</button>
    </div>
  );
}
